import type { Cue, Id, ScriptDocument } from "@supermanus/document-model";
import {
  findCastMember,
  formatDuration,
  formatEpisodeDisplayTitle,
  getEpisodeScenes,
  getSceneCues,
  layoutDualColumnSpans,
  listEpisodes,
} from "@supermanus/document-model";
import {
  cueContentPlainText,
  cssColorFromStyle,
  cssColorToHex,
  isCueContentEmpty,
  looksLikeCueHtml,
} from "./cueRichText";
import {
  formatDocumentEpisodeLine,
  formatDocumentTimestamp,
} from "./DocumentHead";
import {
  cueShowsRowRule,
  isAudioOnlyColumnOrder,
  type RowRuleMode,
  type ScriptColumnOrder,
} from "./editorSettings";
import { DEFAULT_SCENE_HEADER_BG, normalizeHex } from "./printPageLayout";
import { SHOW_REPLIK_UI } from "./replikFeature";

/**
 * In «Kun lyd» print/export, omit cues that only have billedside content
 * (or are entirely blank on the audio side).
 */
export function isEmptyForAudioOnlyPrint(cue: Cue): boolean {
  if (!isCueContentEmpty(cue.audio.content)) return false;
  if (SHOW_REPLIK_UI && cue.audio.characterName?.trim()) return false;
  return true;
}

export type ExportRun = {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
  super?: boolean;
  sub?: boolean;
  color?: string;
  break?: boolean;
};

export type ExportBlock = {
  runs: ExportRun[];
};

export type ExportCell = {
  merge: "restart" | "continue";
  span: number;
  blocks: ExportBlock[];
  characterName?: string;
  color?: string;
  ruleBelow?: boolean;
  meta?: string[];
};

export type ExportTableRow = {
  cells: ExportCell[];
  ruleBelow: boolean;
};

export type ExportScene = {
  number: string | number;
  heading: string;
  title: string;
  duration: string;
  headerBg: string;
  rows: ExportTableRow[];
};

export type ManuscriptExportModel = {
  productionTitle: string;
  episodeLine: string;
  printedAtLabel: string;
  columnLabels: string[];
  showColumnRule: boolean;
  audioOnly: boolean;
  scenes: ExportScene[];
};

export type ManuscriptExportOptions = {
  columnOrder?: ScriptColumnOrder;
  showColumnRule?: boolean;
  rowRuleMode?: RowRuleMode;
  showHours?: boolean;
  showFrames?: boolean;
  fps?: number;
  showSetupNumbers?: boolean;
  showCameraNotes?: boolean;
  showPosition?: boolean;
  defaultSceneHeaderBg?: string;
  printedAt?: Date;
  showEpisodeNumbers?: boolean;
  /** When set, only these scenes are included (order follows the episode). */
  sceneIds?: Id[];
};

type WalkStyle = {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
  super?: boolean;
  sub?: boolean;
  color?: string;
};

function pushRun(runs: ExportRun[], text: string, style: WalkStyle) {
  if (!text) return;
  const last = runs[runs.length - 1];
  if (
    last &&
    !last.break &&
    Boolean(last.bold) === Boolean(style.bold) &&
    Boolean(last.italic) === Boolean(style.italic) &&
    Boolean(last.underline) === Boolean(style.underline) &&
    Boolean(last.strike) === Boolean(style.strike) &&
    Boolean(last.super) === Boolean(style.super) &&
    Boolean(last.sub) === Boolean(style.sub) &&
    (last.color || undefined) === (style.color || undefined)
  ) {
    last.text += text;
    return;
  }
  runs.push({ text, ...style });
}

function walkInline(node: Node, style: WalkStyle, runs: ExportRun[]) {
  if (node.nodeType === Node.TEXT_NODE) {
    pushRun(runs, node.textContent ?? "", style);
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;
  const el = node as Element;
  const tag = el.tagName.toLowerCase();
  if (tag === "br") {
    runs.push({ text: "", break: true });
    return;
  }
  const next: WalkStyle = { ...style };
  if (tag === "strong" || tag === "b") next.bold = true;
  if (tag === "em" || tag === "i") next.italic = true;
  if (tag === "u") next.underline = true;
  if (tag === "s" || tag === "strike" || tag === "del") next.strike = true;
  if (tag === "sup") next.super = true;
  if (tag === "sub") next.sub = true;
  const spanColor = cssColorToHex(cssColorFromStyle(el.getAttribute("style")));
  if (spanColor) next.color = spanColor;
  for (const child of Array.from(el.childNodes)) walkInline(child, next, runs);
}

export function htmlToBlocks(html: string): ExportBlock[] {
  if (typeof DOMParser === "undefined") {
    return plainToBlocks(cueContentPlainText(html));
  }
  const parsed = new DOMParser().parseFromString(
    `<div id="sm-export-root">${html}</div>`,
    "text/html",
  );
  const root = parsed.getElementById("sm-export-root");
  if (!root) return [{ runs: [] }];

  const blocks: ExportBlock[] = [];
  const flush = (nodes: Node[]) => {
    const runs: ExportRun[] = [];
    for (const n of nodes) walkInline(n, {}, runs);
    if (runs.length) blocks.push({ runs });
  };

  let pending: Node[] = [];
  for (const child of Array.from(root.childNodes)) {
    if (
      child.nodeType === Node.ELEMENT_NODE &&
      (child as Element).tagName.toLowerCase() === "p"
    ) {
      if (pending.length) {
        flush(pending);
        pending = [];
      }
      const runs: ExportRun[] = [];
      walkInline(child, {}, runs);
      blocks.push({
        runs: runs.length ? runs : [{ text: "\u00A0" }],
      });
    } else {
      pending.push(child);
    }
  }
  if (pending.length) flush(pending);
  return blocks.length ? blocks : [{ runs: [] }];
}

export function plainToBlocks(text: string): ExportBlock[] {
  const lines = text.length === 0 ? [""] : text.split("\n");
  return lines.map((line) => ({
    runs: line ? [{ text: line }] : [{ text: "\u00A0" }],
  }));
}

export function contentToBlocks(content: string): ExportBlock[] {
  if (!content) return [{ runs: [] }];
  return looksLikeCueHtml(content) ? htmlToBlocks(content) : plainToBlocks(content);
}

function sideMeta(
  doc: ScriptDocument,
  cue: Cue,
  column: "visual" | "audio",
): { characterName?: string; color?: string } {
  const name = (column === "audio" ? cue.audio : cue.visual).characterName?.trim();
  if (!SHOW_REPLIK_UI || !name) return {};
  const cast = findCastMember(doc, name);
  const showName = cast ? cast.showNameAboveLines : true;
  return {
    characterName: showName ? name : undefined,
    color: cast?.color?.trim() || undefined,
  };
}

function cellMetaLines(
  sceneNumber: string | number,
  setupIndex: number,
  cue: Cue,
  opts: ManuscriptExportOptions,
  side: "col1" | "col2" | "audio-only",
): string[] {
  const showCol1 = Boolean(opts.showSetupNumbers || opts.showCameraNotes);
  const showPos = Boolean(opts.showPosition);
  if (!showCol1 && !showPos) return [];
  const kam =
    cue.cameraNumber && cue.cameraNumber >= 1 ? cue.cameraNumber : undefined;
  const kamNote = cue.cameraNote?.trim() ?? "";
  const posNote = cue.positionNote?.trim() ?? "";
  const lines: string[] = [];
  const setup =
    opts.showSetupNumbers ? `${sceneNumber}-${setupIndex}` : "";
  const camera =
    opts.showCameraNotes && (kam != null || kamNote)
      ? [kam != null ? `KAM ${kam}` : null, kamNote || null]
          .filter(Boolean)
          .join(" ")
      : "";
  const pos = showPos ? (posNote ? `Pos. ${posNote}` : "Pos.") : "";
  if (side === "audio-only") {
    if (setup) lines.push(setup);
    if (camera) lines.push(camera);
    if (pos) lines.push(pos);
    return lines;
  }
  if (side === "col1") {
    if (setup) lines.push(setup);
    if (camera) lines.push(camera);
    if (!showCol1 && pos) lines.push(pos);
    return lines;
  }
  if (pos) lines.push(pos);
  else {
    if (setup) lines.push(setup);
    if (camera) lines.push(camera);
  }
  return lines;
}

function buildCell(
  doc: ScriptDocument,
  cue: Cue,
  column: "visual" | "audio",
  span: number,
  merge: "restart" | "continue",
  ruleBelow: boolean,
  meta: string[],
): ExportCell {
  if (merge === "continue") {
    return { merge, span: 1, blocks: [{ runs: [] }] };
  }
  const side = cue[column];
  const metaInfo = sideMeta(doc, cue, column);
  return {
    merge,
    span,
    blocks: isCueContentEmpty(side.content)
      ? [{ runs: [] }]
      : contentToBlocks(side.content),
    characterName: metaInfo.characterName,
    color: metaInfo.color,
    ruleBelow,
    meta,
  };
}

export function buildManuscriptExport(
  doc: ScriptDocument,
  episodeId?: Id | null,
  options: ManuscriptExportOptions = {},
): ManuscriptExportModel | null {
  const episodes = listEpisodes(doc);
  const episode =
    (episodeId && doc.episodes[episodeId]) || episodes[0] || null;
  if (!episode) return null;

  const season =
    episode.seasonId && doc.seasons[episode.seasonId]
      ? doc.seasons[episode.seasonId]
      : null;
  const printedAt = options.printedAt ?? new Date();
  const columnOrder = options.columnOrder ?? "visual-audio";
  const audioOnly = isAudioOnlyColumnOrder(columnOrder);
  const visualIsLeft = columnOrder !== "audio-visual";
  const durFmt = {
    showHours: options.showHours,
    showFrames: options.showFrames,
    fps: options.fps,
  };
  const defaultBg = normalizeHex(
    options.defaultSceneHeaderBg,
    DEFAULT_SCENE_HEADER_BG,
  );

  const columnLabels = audioOnly
    ? ["Lydside"]
    : visualIsLeft
      ? ["Billedside", "Lydside"]
      : ["Lydside", "Billedside"];

  const scenes: ExportScene[] = [];
  const onlyIds = options.sceneIds?.length ? new Set(options.sceneIds) : null;
  for (const scene of getEpisodeScenes(doc, episode.id)) {
    if (onlyIds && !onlyIds.has(scene.id)) continue;
    const cues = getSceneCues(doc, scene.id);
    const spans = layoutDualColumnSpans(cues);
    const rows: ExportTableRow[] = [];

    for (let i = 0; i < cues.length; i++) {
      const cue = cues[i]!;
      const visualSpan = spans[i]?.visualSpan ?? 1;
      const audioSpan = spans[i]?.audioSpan ?? 1;
      const setupIndex = i + 1;
      const rowRule = cueShowsRowRule(cue, options.rowRuleMode ?? "custom");

      if (audioOnly) {
        if (isEmptyForAudioOnlyPrint(cue)) continue;
        const col1Meta = cellMetaLines(
          scene.number,
          setupIndex,
          cue,
          options,
          "audio-only",
        );
        const audioCell = buildCell(
          doc,
          cue,
          "audio",
          1,
          "restart",
          rowRule,
          col1Meta,
        );
        rows.push({ cells: [audioCell], ruleBelow: rowRule });
        continue;
      }

      let visualRuleBelow = false;
      if (visualSpan > 1) {
        const endCue = cues[i + visualSpan - 1];
        visualRuleBelow = cueShowsRowRule(endCue, options.rowRuleMode ?? "custom");
      } else if (visualSpan === 1) {
        visualRuleBelow = cueShowsRowRule(cue, options.rowRuleMode ?? "custom");
      }

      const bothCells = visualSpan > 0 && audioSpan > 0;
      const col1Meta = cellMetaLines(
        scene.number,
        setupIndex,
        cue,
        options,
        "col1",
      );
      const col2Meta = cellMetaLines(
        scene.number,
        setupIndex,
        cue,
        options,
        "col2",
      );
      const showMeta = Boolean(
        options.showSetupNumbers || options.showCameraNotes || options.showPosition,
      );

      const visualCell =
        visualSpan > 0
          ? buildCell(
              doc,
              cue,
              "visual",
              visualSpan,
              "restart",
              visualRuleBelow,
              visualIsLeft
                ? showMeta && (options.showSetupNumbers || options.showCameraNotes || (options.showPosition && bothCells))
                  ? col1Meta
                  : []
                : showMeta && (options.showPosition || ((options.showSetupNumbers || options.showCameraNotes) && bothCells))
                  ? col2Meta
                  : [],
            )
          : buildCell(doc, cue, "visual", 1, "continue", false, []);

      const audioCell =
        audioSpan > 0
          ? buildCell(
              doc,
              cue,
              "audio",
              audioSpan,
              "restart",
              rowRule,
              visualIsLeft
                ? showMeta && (options.showPosition || ((options.showSetupNumbers || options.showCameraNotes) && bothCells))
                  ? col2Meta
                  : []
                : showMeta && (options.showSetupNumbers || options.showCameraNotes || (options.showPosition && bothCells))
                  ? col1Meta
                  : [],
            )
          : buildCell(doc, cue, "audio", 1, "continue", false, []);

      const cells = visualIsLeft
        ? [visualCell, audioCell]
        : [audioCell, visualCell];

      rows.push({ cells, ruleBelow: rowRule });
    }

    scenes.push({
      number: scene.number,
      heading: scene.heading?.trim() ?? "",
      title: scene.title,
      duration: formatDuration(scene.durationSeconds, durFmt),
      headerBg: normalizeHex(scene.headerBackground, defaultBg),
      rows,
    });
  }

  return {
    productionTitle: doc.production.title.trim(),
    episodeLine: formatDocumentEpisodeLine(
      season?.title,
      formatEpisodeDisplayTitle(episode, Boolean(options.showEpisodeNumbers)),
      "script",
    ),
    printedAtLabel: formatDocumentTimestamp(printedAt),
    columnLabels,
    showColumnRule: Boolean(options.showColumnRule) && !audioOnly,
    audioOnly,
    scenes,
  };
}

export function suggestedExportFilename(
  doc: ScriptDocument,
  episodeId: Id | null | undefined,
  variant: "full" | "audio",
  ext: "rtf" | "docx",
): string {
  const episodes = listEpisodes(doc);
  const episode =
    (episodeId && doc.episodes[episodeId]) || episodes[0] || null;
  const base = [doc.production.title, episode?.title]
    .filter(Boolean)
    .join(" – ")
    .replace(/[\\/:*?"<>|]+/g, "-")
    .trim();
  const stem = base || "manus";
  const name = variant === "audio" ? `${stem} – lydside` : stem;
  return `${name}.${ext}`;
}
